/**
 * «Om Racecloud» — offentlig salgs-/produktside.
 *
 * Presenterer Racecloud som et produkt for seilforeninger: startprosedyrer,
 * GPS-sporing og reprise. Priser er foreløpig ikke fastsatt (satt til kr 0).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SiteLayout } from '../components/SiteLayout.tsx';

const CONTACT_EMAIL = 'terje@nidi.no';

function Feature({ icon, title, children }: { icon: string; title: string; children: ReactNode }) {
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="font-semibold text-lg mb-1.5">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{children}</p>
    </div>
  );
}

function Product({
  icon,
  name,
  price,
  unit,
  children,
}: {
  icon: string;
  name: string;
  price: string;
  unit: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-slate-900/60 border border-slate-700 rounded-2xl p-6 flex flex-col">
      <div className="text-3xl mb-3">{icon}</div>
      <h3 className="font-semibold text-lg">{name}</h3>
      <div className="mt-2 mb-3">
        <span className="text-3xl font-bold tabular-nums">{price}</span>
        <span className="text-slate-400 text-sm"> {unit}</span>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed flex-1">{children}</p>
    </div>
  );
}

export function AboutPage() {
  return (
    <SiteLayout>
      <main className="max-w-4xl mx-auto px-4 pb-16">
        {/* Hero */}
        <section className="text-center pt-10 pb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Racecloud</h1>
          <p className="mt-3 text-xl text-sky-300 font-semibold">
            Regattaer gjort enkelt — for seilforeninger
          </p>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Et komplett system for å arrangere regattaer: enkle og automatiske startprosedyrer,
            live GPS-sporing av båtene, og full avspilling av seilasen i etterkant. Om bord er
            telefonen og appen båtens software — og alle kan følge regattaen live i nettleseren.
          </p>
          <div className="mt-7 flex items-center justify-center gap-3">
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Racecloud for vår forening`}
              className="px-5 py-2.5 bg-sky-600 hover:bg-sky-500 rounded-lg font-semibold"
            >
              Ta kontakt
            </a>
            <Link
              to="/regattaer"
              className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg font-semibold"
            >
              Se det i bruk
            </Link>
          </div>
        </section>

        {/* Tre hovedfunksjoner */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Feature icon="⏱️" title="Automatisk startprosedyre">
            Synkronisert nedtelling, klasseflagg og lydsignaler. Alle deltakere ser nøyaktig samme
            klokke på sin egen telefon — ingen tvil om når startsignalet går.
          </Feature>
          <Feature icon="🛰️" title="Live GPS-sporing">
            Følg flåten i sanntid på Kartverkets sjøkart. Spor med appen på telefonen, eller med en
            fast GPS-tracker om bord for presis, automatisk posisjon.
          </Feature>
          <Feature icon="⏮️" title="Reprise etterpå">
            Spill av hele regattaen i etterkant — studer start, taktikk og målgang om igjen, båt
            for båt.
          </Feature>
        </section>

        {/* Alt på ett sted */}
        <section className="mt-12">
          <h2 className="text-2xl font-bold text-center mb-6">Alt på ett sted</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl mx-auto text-sm text-slate-300">
            {[
              ['🚩', 'Klasser med egne signalflagg'],
              ['⛵', 'Båt- og foreningsregister'],
              ['🏁', 'Tidtaking og resultater'],
              ['🗺️', 'Live kart med Kartverkets sjøkart'],
              ['📱', 'Appen om bord: startklokke og startlinje'],
              ['🏟️', 'Flere foreninger på samme plattform'],
            ].map(([icon, text]) => (
              <li key={text} className="flex items-center gap-3">
                <span className="text-lg shrink-0">{icon}</span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Produkter og priser */}
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-center">Produkter og priser</h2>
          <p className="text-center text-slate-500 text-sm mt-2 mb-6">
            Priser er foreløpig ikke fastsatt.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Product icon="🏟️" name="Foreningslisens" price="kr 0" unit="pr. år">
              Full tilgang til hele systemet for foreningen: ubegrenset antall regattaer, båter og
              deltakere, startprosedyrer, live kart, reprise og resultater.
            </Product>
            <Product icon="🛰️" name="GPS-tracker inkl. simkort" price="kr 0" unit="pr. enhet">
              Maskinvare-tracker med ferdig konfigurert SIM, lagt inn på båten. SIM-et er en
              tvilling til ditt eget telefonabonnement — klar til bruk rett ut av esken.
            </Product>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-14 bg-slate-900/60 border border-slate-800 rounded-2xl p-8 text-center">
          <h2 className="text-xl font-bold">Vil din forening prøve Racecloud?</h2>
          <p className="mt-2 text-slate-400 max-w-xl mx-auto">
            Ta kontakt, så hjelper vi dere i gang med foreningen, båtregisteret og den første
            regattaen.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Racecloud for vår forening`}
            className="mt-5 inline-block px-6 py-3 bg-sky-600 hover:bg-sky-500 rounded-lg font-semibold"
          >
            Ta kontakt
          </a>
        </section>
      </main>
    </SiteLayout>
  );
}
